import { intersection } from 'lodash';
import { keys, pick, pipe, head, toUpper, toLower } from 'ramda';
import * as RA from 'ramda-adjunct';
import { wait } from '.';

/**
 * Produces a getter object. Mimics Vuex Getter Structure
 * @param {{[string]: Function}} transformations Transformation Functions Dictionary 
 */
export const getter = transformations => state => {
  return keys(transformations).reduce((accm, cur) => {
    accm[cur] = transformations[cur](state);
    return accm;
  }, {});
}

/** Picks from the object and rename the properties by the keyMap  */
export const pickAs = (keyMap) => pipe(
  pick(keys(keyMap)),
  RA.renameKeys(keyMap)
);

/** Reverses the JSON objects key-value pairs. Keys becomes values, values becomes keys */
export const reverseKeyValue = (obj) => keys(obj).reduce((accumulator, cur) => ({
  ...accumulator,
  [obj[cur]]: cur
}), {});

export const rejectNotString = (value) => {
  if (!value) {
    throw Error('Provide a non-falsy string');
  } else if (typeof value === 'string') {
    if (value.length > 0) {
      return value;
    } else {
      throw Error('Provide a non-empty string');
    }
  } else {
    throw Error('Provide a valid string');
  }
}

/** Return the value in string format - If not possible returns empty string */
export const ensureString = (value, defaultString = '') => {
  if (value) {
    return `${value}`;
  } else {
    return defaultString;
  }
}

/** Return the value in BoolInt - If not possible returns default Int / 0 */
export const ensureBoolInt = (value, defaultInt = 0) => {
  const val = Number(value);
  if (val === 1 || val === 0) {
    return val;
  } else {
    return defaultInt;
  }
}

/** Ensures that all the values of the obejct are string or atleast the default value */
export const ensureStringObject = (obj, defaultString = '') => {
  return keys(obj)
    .map(key => [key, ensureString(obj[key], defaultString)])
    .reduce((accm, cur) => {
      accm[cur[0]] = cur[1];
      return accm;
    }, {});
}
/** Ensures all values of the Object is String, Takes in the Keys of the Object as String Array */
export const ensureStringObjectFromKeys = (obj, keyList, defaultString = '') => {
  return keys(pick(keyList, obj))
    .map(key => [key, ensureString(obj[key], defaultString)])
    .reduce((accm, cur) => {
      accm[cur[0]] = cur[1];
      return accm;
    }, {});
}
/** Ensures all values of the Object is boolean 1/0, Takes in the Keys of the Object as String Array */
export const ensureBoolIntObjectFromKeys = (obj, keyList, defaultInt = 0) => {
  return keys(pick(keyList, obj))
    .map(key => [key, ensureBoolInt(obj[key], defaultInt)])
    .reduce((accm, cur) => {
      accm[cur[0]] = cur[1];
      return accm;
    }, {});
}

/** Converts the array of keyvalues to Object */
export const keyValuesToObj = (keyValueArray) => {
  return keyValueArray.reduce((accm, cur) => {
    accm[cur[0]] = cur[1];
    return accm;
  }, {});
}


/** Split the first half of the string - by space*/
export const firstHalfString = (string, delimiter = ' ') => {
  return string && string.split(delimiter) && string.split(delimiter)[0];
}

/** Abbreviate the string limited by MaxLength */
export const abbreviate = (string = '', maxLength = 6) => {
  const trimmedString = string.trim();
  if (trimmedString.length <= maxLength) {
    return trimmedString;
  }
  const words = trimmedString.split(' ');
  let initials = words.map(w => head(w));
  if (initials.length > maxLength) {
    return initials.slice(0, maxLength).join('');
  }
  const maxIn = maxLength / words.length;
  const extra = maxLength % words.length;
  // initials = words.map(w => head(w).toUpperCase()+w.slice(1, maxIn).toLowerCase());
  initials = words.map(w => '');
  let i = 0;
  const WC = words.map(w => 0);
  while (i < maxLength) {
    words.map((w, index) => {
      if (w[WC[index]] && i < maxLength) {
        // console.log({'abb.mjs:26 WC' : WC, 'w[WC[index]]': w[WC[index]]});
        // console.log(words, w, index, initials)
        initials[index] += WC[index] === 0 ? toUpper(w[WC[index]]) : toLower(w[WC[index]]);
        WC[index]++;
        i++;

      }
    })
    // If there's no more letters to add, then break the loop. If WC counts are same as words lengths, then break the loop
    if (WC.every((wc, index) => wc === words[index].length)) {
      break;
    }

    // console.log('loop', initials)
  }
  // console.log(initials)
  return initials.join('');
}

/** Returns a random number between max and min INCLUSIVE */
export const random = (max = 100, min = 0) => {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

/** Generate a new ID from the list of IDs. The ID is negative - to detect new set of IDs. Pass in negative=false for getting a positive next ID */
export const generateNextId = (numbers = [], negative = true) => {
  if (numbers?.length == 0) {
    numbers = [0];
  }
  if (numbers?.length > 0) {
    if (typeof numbers[0] != 'number' || isNaN(numbers[0])) {
      throw Error('Pass in a Array of Number in the GenerateNextId Function');
    }
  }

  const max = negative ? Math.max(...numbers.map(Math.abs)) : Math.max(...numbers);
  const newMax = (Math.abs(max) + 1) * (negative ? -1 : 1);
  return newMax;
}
/** Detects if the value is negative number or not */
export const isNegative = (value) => {
  if (!isNaN(value)) {
    if (Math.sign(value) === -1) {
      return true;
    } else {
      return false;
    }
  } else {
    return false;
  }
}

// console.log(abbreviate('Hackney Wick Studio'));
// console.log(abbreviate('Hac Wick Studio', 10));
// console.log(abbreviate('Hac Wick Studio Lon', 3));
// console.log(abbreviate('London'));

export const cycleArrayNext = (arr, val) => {
  arr = arr.sort((a, b) => a - b);

  for (let i = 0; i < arr.length; i++) {
    if (val < arr[i]) {
      return arr[i];
    }
  }
  return arr[0];
}


/** Converts a JSON object to FormData */
export function convertToFormData(object) {
  const formData = new FormData();
  Object.keys(object).forEach(key => formData.append(key, object[key]));
  return formData;
}




/**
 * @description Fetches the properties of the SidewindowDetails Object with the help of 
 * NavTabHelper with the API Args. 
 * API Args is an Array of which the First item must be the Primary Key of The SidewindowDetails Dictionary.
 * The later Arguments Must be put inside the Array if neccessary.
 * @param  {} NavTabHelper
 * @param  {} propertyAPIMap
 * @param  {} properties
 * @param  {Array<any>} apiArgs=[primary_key, ...otherArgs]
 * @param  {} parallel=true
 */
export async function fetchPropertiesWithArgs(NavTabHelper, propertyAPIMap, properties, apiArgs, parallel = true, delay = 100, setnull = false) {
  let logs = [];
  const [primary_key, ...otherArgs] = apiArgs;
  const promises = properties.map(property => {
    const propertyAPI = propertyAPIMap[property];
    if (!propertyAPI) {
      return Promise.resolve();
    }
    NavTabHelper.setSingleNavTabDetails(primary_key, (detailsDict, singleDetails) => ({
      ...singleDetails,
      [`${property}_loading`]: true,
      [property]: setnull ? null : singleDetails[property],
    }));
    return propertyAPI(primary_key, ...otherArgs).then(detailsRes => {
      NavTabHelper.setSingleNavTabDetails(primary_key, (detailsDict, singleDetails) => ({
        ...singleDetails,
        [`${property}_loading`]: false,
        [property]: detailsRes?.data,
      }));
      const logData = JSON.parse(JSON.stringify({ [property]: detailsRes?.data }));
      logs.push(logData);
    });
  });

  if (parallel) {
    await Promise.all(promises);
  } else {
    for (const promise of promises) {
      await promise;
      await wait(delay);
    }
  }

  console.log({ 'logs': logs });
}
//fe

export async function fetchPropertiesWithArgsFlat(NavTabHelper, propertyAPIMap, properties, apiArgs, parallel = true, delay = 100, setnull = false) {
  let logs = [];
  const [primary_key, ...otherArgs] = apiArgs;
  const promises = properties.map(property => {
    const propertyAPI = propertyAPIMap[property];
    if (!propertyAPI) {
      return Promise.resolve();
    }
    NavTabHelper.setSingleNavTabDetails(primary_key, (detailsDict, singleDetails) => ({
      ...singleDetails,
      [`${property}_loading`]: true,
      // [property]: setnull ? null : singleDetails[property],
    }));
    return propertyAPI(primary_key, ...otherArgs).then(detailsRes => {
      NavTabHelper.setSingleNavTabDetails(primary_key, (detailsDict, singleDetails) => ({
        ...singleDetails,
        [`${property}_loading`]: false,
        ...detailsRes?.data,
      }

      ));
      const logData = JSON.parse(JSON.stringify({ [property]: detailsRes?.data }));
      logs.push(logData);
    });
  });

  if (parallel) {
    await Promise.all(promises);
  } else {
    for (const promise of promises) {
      await promise;
      await wait(delay);
    }
  }

  console.log({ 'logs': logs });
}